import React, { useEffect, useState, useCallback } from 'react';
import { api, useAuth } from '../auth';
import { Icon, SensitivityBadge, RoleBadge, EmptyState } from '../components/ui';

const statusStyle = {
  Pending: 'text-secondary bg-secondary-container/40',
  Approved: 'text-on-tertiary-container bg-emerald-100',
  Denied: 'text-error bg-error-container',
};

export default function Requests() {
  const { user } = useAuth();

  // Department Heads (Management) and Admins both get access to the approval queue
  const isApprover = user.role === 'Admin' || user.role === 'Management';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Split state for the UI: default to queue for approvers, mine for regular users
  const [tab, setTab] = useState(isApprover ? 'queue' : 'mine');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/access-requests');
      setRequests(data.requests);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    await api.post(`/access-requests/${id}/decide`, { decision });
    load();
  };

  // Filter requests based on the active tab
  const displayRequests = requests.filter(r =>
    tab === 'mine' ? String(r.requestedBy?._id) === String(user._id || user.id) : String(r.requestedBy?._id) !== String(user._id || user.id)
  );

  return (
    <>
      <div className="flex items-center justify-between mb-lg">
        <h2 className="font-headline-md text-headline-md text-primary">
          Access Requests
        </h2>

        {isApprover && (
          <div className="flex bg-surface-container-low rounded-md border border-outline-variant overflow-hidden">
            <button onClick={() => setTab('queue')} className={`px-4 py-2 text-sm font-bold transition-colors ${tab === 'queue' ? 'bg-primary text-white' : 'text-on-surface-variant hover:bg-surface-container-high'}`}>
              Approval Queue
            </button>
            <button onClick={() => setTab('mine')} className={`px-4 py-2 text-sm font-bold transition-colors ${tab === 'mine' ? 'bg-primary text-white' : 'text-on-surface-variant hover:bg-surface-container-high'}`}>
              My Requests
            </button>
          </div>
        )}
      </div>

      <div className="bg-surface-container-lowest border border-outline-variant rounded-lg shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-gutter px-md py-3 bg-surface-container-low border-b border-outline-variant">
          <div className="col-span-4 font-label-md text-label-md text-on-surface-variant uppercase tracking-widest">Asset</div>
          <div className="col-span-3 font-label-md text-label-md text-on-surface-variant uppercase tracking-widest">Requested By</div>
          <div className="col-span-3 font-label-md text-label-md text-on-surface-variant uppercase tracking-widest">Reason</div>
          <div className="col-span-2 text-right font-label-md text-label-md text-on-surface-variant uppercase tracking-widest">Status</div>
        </div>

        {loading ? (
          <div className="p-xl text-center text-on-surface-variant">Loading…</div>
        ) : displayRequests.length === 0 ? (
          <EmptyState icon="inbox" title={tab === 'queue' ? "No pending approvals" : "No active requests"} subtitle={tab === 'queue' ? "Your queue is clear." : "Request access from any restricted asset."} />
        ) : (
          <div className="divide-y divide-outline-variant">
            {displayRequests.map((r) => (
              <div key={r._id} className="grid grid-cols-12 gap-gutter px-md py-3 items-center">
                <div className="col-span-4 flex items-center gap-2 min-w-0">
                  <Icon name="description" className="text-secondary shrink-0" />
                  <div className="min-w-0">
                    <p className="font-body-md text-body-md font-semibold text-primary truncate">{r.asset?.filename || '—'}</p>
                    <p className="text-[10px] text-on-surface-variant uppercase font-bold mt-0.5">REQ: {r.kind}</p>
                  </div>
                </div>

                <div className="col-span-3 min-w-0">
                  <p className="font-body-md text-body-md truncate">{r.requestedBy?.name}</p>
                  <RoleBadge role={r.requestedBy?.role} />
                </div>

                <div className="col-span-3 font-body-sm text-body-sm text-on-surface-variant truncate">
                  {r.reason || <span className="italic opacity-60">No reason provided</span>}
                </div>
                <div className="col-span-2 flex justify-end items-center gap-2">
                  {isApprover && tab === 'queue' && r.status === 'Pending' ? (
                    <>
                      <button onClick={() => decide(r._id, 'Approved')} title="Approve" className="p-1.5 rounded text-on-tertiary-container hover:bg-emerald-100">
                        <Icon name="check_circle" fill={1} />
                      </button>
                      <button onClick={() => decide(r._id, 'Denied')} title="Deny" className="p-1.5 rounded text-error hover:bg-error-container">
                        <Icon name="cancel" fill={1} />
                      </button>
                    </>
                  ) : (
                    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-tight ${statusStyle[r.status]}`}>
                      {r.status}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}